import { useEffect, useState } from 'react';
import { createQuoteRequest } from './api/api';
import './QuoteRequestForm.css';

export default function QuoteRequestForm({ isOpen, onClose, product = null }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    postcode: '',
    installationArea: '',
    flooringType: product?.category || '',
    areaSize: '',
    message: '',
    requiresMeasurement: 'No',
    address: '',
    preferredDate: '',
    preferredTime: '',
    productId: product?.id || null,
    productName: product?.name || null
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setFormData(prev => ({
      ...prev,
      flooringType: product?.category || prev.flooringType,
      productId: product?.id || null,
      productName: product?.name || null
    }));
  }, [isOpen, product]);

  const installationAreas = [
    'Living Room',
    'Bedroom',
    'Kitchen',
    'Hallway',
    'Office / Commercial',
    'Whole Property',
    'Other'
  ];

  const flooringTypes = [
    'SPC Flooring',
    'LVT Flooring',
    'Herringbone SPC Flooring',
    'Artificial Grass',
    'Carpet',
    'Not Sure'
  ];

  const preferredTimes = [
    'Morning',
    'Afternoon',
    'Flexible'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[\d\s+()-]+$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone number';
    }
    if (!formData.postcode.trim()) newErrors.postcode = 'Postcode is required';
    if (!formData.installationArea) newErrors.installationArea = 'Installation area is required';
    if (!formData.flooringType) newErrors.flooringType = 'Flooring type is required';

    if (formData.requiresMeasurement === 'Yes') {
      if (!formData.address.trim()) newErrors.address = 'Address is required for measurement service';
      if (!formData.preferredDate) newErrors.preferredDate = 'Preferred date is required';
      if (!formData.preferredTime) newErrors.preferredTime = 'Preferred time is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await createQuoteRequest(formData);
      setSubmitted(true);
    } catch (error) {
      alert('Failed to submit quote request: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSubmitted(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      postcode: '',
      installationArea: '',
      flooringType: product?.category || '',
      areaSize: '',
      message: '',
      requiresMeasurement: 'No',
      address: '',
      preferredDate: '',
      preferredTime: '',
      productId: product?.id || null,
      productName: product?.name || null
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  if (submitted) {
    return (
      <div className="quote-modal-overlay" onClick={handleClose}>
        <div className="quote-modal-content quote-success" onClick={(e) => e.stopPropagation()}>
          <button className="quote-modal-close" onClick={handleClose}>&times;</button>
          <div className="success-checkmark">✓</div>
          <h2 className="success-title">Request Received</h2>
          <p className="success-message">
            Thank you for your enquiry. We will contact you shortly at <strong>{formData.email}</strong> or <strong>{formData.phone}</strong>.
          </p>
          {product && (
            <p className="success-product">
              <span className="eyebrow">Product Requested</span>
              <span className="product-name">{product.name}</span>
            </p>
          )}
          <button onClick={handleClose} className="btn btn-dark">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="quote-modal-overlay" onClick={handleClose}>
      <div className="quote-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="quote-modal-close" onClick={handleClose}>&times;</button>

        <div className="quote-modal-header">
          <span className="eyebrow">Get a Quote</span>
          <h2 className="quote-modal-title">Request Quotation</h2>
          <div className="rule" />
          {product && (
            <div className="product-badge">
              <span className="badge-label">Product:</span>
              <span className="badge-value">{product.name}</span>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="quote-form">
          <div className="form-section">
            <h3 className="form-section-title">Contact Information</h3>

            <div className="form-group">
              <label>Full Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={errors.name ? 'error' : ''}
              />
              {errors.name && <span className="error-text">{errors.name}</span>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={errors.email ? 'error' : ''}
                />
                {errors.email && <span className="error-text">{errors.email}</span>}
              </div>

              <div className="form-group">
                <label>Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={errors.phone ? 'error' : ''}
                />
                {errors.phone && <span className="error-text">{errors.phone}</span>}
              </div>
            </div>

            <div className="form-group">
              <label>Postcode *</label>
              <input
                type="text"
                name="postcode"
                value={formData.postcode}
                onChange={handleChange}
                className={errors.postcode ? 'error' : ''}
              />
              {errors.postcode && <span className="error-text">{errors.postcode}</span>}
            </div>
          </div>

          <div className="form-section">
            <h3 className="form-section-title">Project Details</h3>

            <div className="form-row">
              <div className="form-group">
                <label>Installation Area *</label>
                <select
                  name="installationArea"
                  value={formData.installationArea}
                  onChange={handleChange}
                  className={errors.installationArea ? 'error' : ''}
                >
                  <option value="">Select area</option>
                  {installationAreas.map(area => (
                    <option key={area} value={area}>{area}</option>
                  ))}
                </select>
                {errors.installationArea && <span className="error-text">{errors.installationArea}</span>}
              </div>

              <div className="form-group">
                <label>Flooring Type *</label>
                <select
                  name="flooringType"
                  value={formData.flooringType}
                  onChange={handleChange}
                  className={errors.flooringType ? 'error' : ''}
                  disabled={!!product}
                >
                  <option value="">Select type</option>
                  {flooringTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                {errors.flooringType && <span className="error-text">{errors.flooringType}</span>}
              </div>
            </div>

            <div className="form-group">
              <label>Area Size (sqm)</label>
              <input
                type="text"
                name="areaSize"
                value={formData.areaSize}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Additional Details</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="3"
              />
            </div>
          </div>

          <div className="form-section">
            <h3 className="form-section-title">Measurement Service</h3>

            <div className="radio-group">
              <label className="radio-option">
                <input
                  type="radio"
                  name="requiresMeasurement"
                  value="No"
                  checked={formData.requiresMeasurement === 'No'}
                  onChange={handleChange}
                />
                <span>No, I have measurements</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="requiresMeasurement"
                  value="Yes"
                  checked={formData.requiresMeasurement === 'Yes'}
                  onChange={handleChange}
                />
                <span>Yes, I need measurement service</span>
              </label>
            </div>

            {formData.requiresMeasurement === 'Yes' && (
              <div className="measurement-fields">
                <div className="form-group">
                  <label>Property Address *</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className={errors.address ? 'error' : ''}
                    rows="2"
                  />
                  {errors.address && <span className="error-text">{errors.address}</span>}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Preferred Date *</label>
                    <input
                      type="date"
                      name="preferredDate"
                      value={formData.preferredDate}
                      onChange={handleChange}
                      className={errors.preferredDate ? 'error' : ''}
                      min={new Date().toISOString().split('T')[0]}
                    />
                    {errors.preferredDate && <span className="error-text">{errors.preferredDate}</span>}
                  </div>

                  <div className="form-group">
                    <label>Preferred Time *</label>
                    <select
                      name="preferredTime"
                      value={formData.preferredTime}
                      onChange={handleChange}
                      className={errors.preferredTime ? 'error' : ''}
                    >
                      <option value="">Select time</option>
                      {preferredTimes.map(time => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                    {errors.preferredTime && <span className="error-text">{errors.preferredTime}</span>}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="form-actions">
            <button type="button" onClick={handleClose} className="btn btn-ghost">
              Cancel
            </button>
            <button type="submit" className="btn btn-dark" disabled={loading}>
              {loading ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
